import React, { useEffect, useState } from "react";
import ApplicationUpdateApi from "../../api/ApplicationUpdateApi";
import LicenseApi from "../../api/LicenseApi";
import SuiteApi from "../../api/SuiteApi";
import { ApplicationInfo } from "../../Model/ApplicationInfo";
import { ELicenseStatus, ELicenseType, LicenseInfo } from "../../Model/License";
import { getSuiteState } from "../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import DateUtil from "../../Utility/DateUtil";
import MtiButton from "../Buttons/MtiButton";
import SwitchButton from "../Buttons/SwitchButton";
import Breadcrumb from "../Header/Breadcrumb";
import Header from "../Header/Header";
import LayoutContent from "../LayoutContent";
import ReportAndSupportNavSubMenu from "../Report/ReportAndSupportNavSubMenu";
import ToolsContent from "../Tools/ToolsCommon/ToolsContent";
import LicenseActivationModal, {
  LicenseAction,
} from "./LicenseActivationModal";
import "./Licensing.css";

const HELP_URL = "https://help.formulatrix.com/rock-imager";

export default function Licensing() {
  const suiteState = useTypedSelector((state) => getSuiteState(state));
  const [license, setLicense] = useState({
    clientName: "-",
    productName: "-",
  } as LicenseInfo);
  const [applicationInfo, setApplicationInfo] = useState({
    productVersion: "4.0.0",
    executableDateStamp: "2021-01-01",
    copyrightYear: "2021",
    companyName: "Formulatrix, Inc.",
    supportPhone: "-",
    supportEmail: "-",
  } as ApplicationInfo);
  const [licenseActionType, setLicenseActionType] = useState(
    "request" as LicenseAction
  );
  const [showLicenseActivationModal, setShowLicenseActivationModal] =
    useState(false);

  useEffect(() => {
    LicenseApi.getLicenseInfo().then((value) => {
      setLicense(value);
    });
    SuiteApi.getApplicationInfo().then((value) => {
      setApplicationInfo(value);
    });
  }, []);

  const requestLicense = () => {
    setLicenseActionType("request");
    setShowLicenseActivationModal(true);
  };

  const activateLicense = () => {
    setLicenseActionType("activation");
    setShowLicenseActivationModal(true);
  };

  const setAutomaticCheckUpdate = (isOn: boolean) => {
    ApplicationUpdateApi.setAutomaticCheckUpdate(isOn);
  };

  const checkUpdate = () => {
    ApplicationUpdateApi.checkUpdate();
  };

  let licenseStatus = "";
  let statusColor = "";
  let licenseType = "-";

  if (license.status) {
    const expiredDate = DateUtil.toDate(license.expiredDate);
    const today = new Date();
    const deltaMiliseccond = expiredDate.getTime() - today.getTime();
    const daysDelta = Math.floor(deltaMiliseccond / (1000 * 60 * 60 * 24));
    if (license.status.licenseStatus === ELicenseStatus.Active) {
      licenseStatus =
        daysDelta <= 14 ? `(${daysDelta} Days Left)` : "(Activated)";
      statusColor = daysDelta <= 14 ? "#FF0000" : "#3DA369";
    } else {
      licenseStatus = "(Expired)";
      statusColor = "#FF0000";
    }
    licenseType =
      ELicenseType[license.status.licenseType] +
      " License" +
      (license.status.licenseType === ELicenseType.PlateStorage
        ? " (Limited to " + license.storageCapacity + " slots)"
        : "");
  }

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              {
                title: "Licensing",
                to: "/report-support/licensing",
                disabled: false,
              },
            ]}
          />
        </Header>
      </LayoutContent.Header>
      <LayoutContent.Content>
        <div style={{ marginTop: "9px", display: "flex" }}>
          <div style={{ width: "33%", margin: 8 }}>
            <ToolsContent title="Activation">
              <ToolsContent.Body style={{ padding: 10 }}>
                <div className="update-activation">
                  <div className="text">License</div>
                  <div className="buttons">
                    <MtiButton
                      onClick={requestLicense}
                      style={{
                        width: "180px",
                        fontWeight: 500,
                        letterSpacing: "1.25px",
                        marginRight: 11,
                      }}
                    >
                      REQUEST LICENSE
                    </MtiButton>
                    <MtiButton
                      onClick={activateLicense}
                      style={{
                        width: "180px",
                        fontWeight: 500,
                        letterSpacing: "1.25px",
                      }}
                    >
                      ACTIVATE
                    </MtiButton>
                  </div>
                </div>
              </ToolsContent.Body>
            </ToolsContent>
            <ToolsContent>
              <div style={{ display: "flex" }}>
                <ToolsContent.Heading
                  text="License Status"
                  style={{ margin: 0, marginTop: 16 }}
                />
                <ToolsContent.Heading
                  text={licenseStatus}
                  style={{
                    color: statusColor,
                    margin: 0,
                    marginTop: 16,
                  }}
                />
              </div>
              <ToolsContent.Body>
                <div className="license-container">
                  <div className="license-user">
                    <div className="license-title">License To</div>
                    <p>{license.clientName}</p>
                    <p>{license.clientContact}</p>
                    <p>{license.clientId}</p>
                  </div>
                  <div className="license-type">
                    <div className="license-title">License type(s)</div>
                    <p>{licenseType}</p>
                  </div>
                  <div className="license-expiration">
                    <div className="license-title">
                      Maintenance Expiration Date
                    </div>
                    <p>
                      {DateUtil.toUsFormatString(
                        DateUtil.toDate(license.expiredDate)
                      )}
                    </p>
                  </div>
                  <div className="license-product">
                    <div className="license-title">License Product</div>
                    <p>{license.productName}</p>
                  </div>
                  <div className="license-features">
                    <div className="license-title">License Feature(s)</div>
                    <p>{"(All)"}</p>
                  </div>
                </div>
              </ToolsContent.Body>
            </ToolsContent>
          </div>
          <div style={{ width: "33%", margin: 8 }}>
            <ToolsContent title="Software Update">
              <ToolsContent.Body style={{ padding: 10 }}>
                <div className="update-activation">
                  <div className="toggle-automatic-update">
                    <SwitchButton
                      isOn={suiteState.applicationUpdate.isAutomaticCheckUpdate}
                      onToggle={setAutomaticCheckUpdate}
                      style={{ width: 60, height: 30 }}
                    />
                    <p>Enable Automatic Check Update</p>
                  </div>
                  <div className="buttons">
                    <MtiButton
                      onClick={checkUpdate}
                      style={{
                        width: "180px",
                        fontWeight: 500,
                        letterSpacing: "1.25px",
                      }}
                    >
                      CHECK UPDATE
                    </MtiButton>
                  </div>
                </div>
              </ToolsContent.Body>
            </ToolsContent>
            <ToolsContent style={{ margin: 0, marginTop: 0 }}>
              <ToolsContent.Heading
                text="About"
                style={{ margin: 0, marginTop: 18 }}
              />
              <ToolsContent.Body>
                <div className="software-info">
                  <div className="info-version">
                    <div className="info-title">Version</div>
                    <p>{applicationInfo.productVersion}</p>
                    <p>
                      {DateUtil.toUsFormatString(
                        DateUtil.toDate(applicationInfo.executableDateStamp)
                      )}
                    </p>
                  </div>
                  <div className="info-support">
                    <div className="info-title">Support</div>
                    <p>{applicationInfo.supportPhone}</p>
                    <p>{applicationInfo.supportEmail}</p>
                    <p>{`Copyright ${applicationInfo.copyrightYear} ${applicationInfo.companyName}`}</p>
                  </div>
                  <div className="info-help">
                    <MtiButton
                      style={{
                        width: 180,
                        fontWeight: 500,
                        letterSpacing: "1.25px",
                      }}
                      onClick={() =>
                        window.open(
                          `${HELP_URL}/${applicationInfo.productVersion.slice(
                            0,
                            3
                          )}`,
                          "help-url"
                        )
                      }
                    >
                      HELP TOPICS
                    </MtiButton>
                  </div>
                </div>
              </ToolsContent.Body>
            </ToolsContent>
          </div>
        </div>
        {showLicenseActivationModal && (
          <LicenseActivationModal
            licenseAction={licenseActionType}
            show={showLicenseActivationModal}
            onHide={() => setShowLicenseActivationModal(false)}
          />
        )}
      </LayoutContent.Content>
      <LayoutContent.SubMenu>
        <ReportAndSupportNavSubMenu />
      </LayoutContent.SubMenu>
    </LayoutContent>
  );
}
